import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * All per-song `<head>` tags (canonical url + Open Graph + Twitter card).
 * Kept as a tiny stateless child so SongPage's JSX reads cleanly; any
 * addition here (e.g. `og:audio`, structured data) lives in one place.
 */
const SongPageMeta = ({ songDetails, canonicalSongUrl }) => {
  const title = `${songDetails?.songName} by ${songDetails?.songArtist} | Good Song Club`;
  const image = songDetails?.thumbnailURL
    ? songDetails.thumbnailURL
    : '/logo192.png';

  let absoluteImage = image;
  try {
    const origin = new URL(canonicalSongUrl).origin;
    if (image.startsWith('/')) {
      absoluteImage = `${origin}${image}`;
    } else if (!/^https?:\/\//i.test(image)) {
      absoluteImage = `${origin}/${image}`;
    }
  } catch {
    // keep relative `image` if URL parsing fails
  }

  const hasLargePreview =
    Boolean(songDetails?.thumbnailURL) &&
    /^https?:\/\//i.test(String(songDetails.thumbnailURL));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MusicRecording',
    name: songDetails?.songName,
    byArtist: {
      '@type': 'MusicGroup',
      name: songDetails?.songArtist,
    },
    url: canonicalSongUrl,
    image: absoluteImage,
  };

  return (
    <Helmet>
      <title>{title}</title>
      <link rel="canonical" href={canonicalSongUrl} />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <meta property="og:title" content={title} />
      <meta property="og:description" content={songDetails?.description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="twitter:image" content={absoluteImage} />
      <meta
        property="twitter:card"
        content={hasLargePreview ? 'summary_large_image' : 'summary'}
      />
      <meta property="twitter:title" content={title} />
      <meta
        property="twitter:description"
        content={songDetails?.description}
      />
      <meta property="og:url" content={canonicalSongUrl} />
      <meta property="twitter:url" content={canonicalSongUrl} />
      <meta
        property="og:image:alt"
        content={`Thumbnail for ${songDetails?.songName} by ${songDetails?.songArtist}`}
      />
      <meta property="og:type" content="website" />
    </Helmet>
  );
};

export default SongPageMeta;
